import { eachDayOfInterval, format, isSameDay, parseISO, startOfMonth } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { DayClickEventHandler, DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const DATE_FORMAT = 'yyyy-MM-dd';

export interface BaseCalendarProps {
  /**
   * Array of available date strings in ISO format (e.g., "2026-01-01")
   */
  availableDates: string[];
  /**
   * Currently selected dates
   */
  selectedDates: Date[];
  /**
   * Callback when selected dates change
   */
  onSelectedDatesChange: (dates: Date[]) => void;
  /**
   * Optional array of disabled dates (dates that cannot be selected)
   */
  disabledDates?: Date[];
  /**
   * Optional current month to display (defaults to first selected date or today)
   */
  currentMonth?: Date;
  /**
   * Optional callback when month changes
   */
  onMonthChange?: (date: Date) => void;
  /**
   * Optional number of months to display side by side
   */
  numberOfMonths?: number;
  /**
   * Optional CSS class name
   */
  className?: string;
  /**
   * Optional flag to disable the calendar
   */
  disabled?: boolean;
  /**
   * Optional date from which to start showing dates (e.g. today or start of month)
   */
  fromDate?: Date;
}

export function BaseCalendar({
  availableDates,
  selectedDates,
  onSelectedDatesChange,
  disabledDates = [],
  currentMonth,
  onMonthChange,
  numberOfMonths = 1,
  className,
  disabled = false,
  fromDate,
}: BaseCalendarProps) {
  const [shiftHeld, setShiftHeld] = useState(false);
  const [previousSelectedDate, setPreviousSelectedDate] = useState<Date | null>(null);

  // Default fromDate to the start of the current month if not provided
  const effectiveFromDate = useMemo(() => fromDate ?? startOfMonth(new Date()), [fromDate]);

  // Internal month state - only used if onMonthChange is not provided (uncontrolled mode)
  const [internalMonth, setInternalMonth] = useState<Date>(() => {
    if (currentMonth) return currentMonth;
    if (selectedDates.length > 0) return selectedDates[0];
    return startOfMonth(new Date());
  });

  // Track shift key state for range selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setShiftHeld(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setShiftHeld(false);
        // Clear range start when shift is released
        setPreviousSelectedDate(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Update internal month when currentMonth prop changes (controlled mode)
  useEffect(() => {
    if (currentMonth) {
      setInternalMonth(currentMonth);
    }
  }, [currentMonth]);

  const availableDatesSet = useMemo(() => {
    return new Set(availableDates.map((dateStr) => format(parseISO(dateStr), DATE_FORMAT)));
  }, [availableDates]);

  const availableDatesAsDates = useMemo(() => {
    return availableDates.map((dateStr) => parseISO(dateStr));
  }, [availableDates]);

  const displayMonth = currentMonth ?? internalMonth;

  const handleMonthChange = (date: Date) => {
    if (onMonthChange) {
      // Controlled mode - parent handles state
      onMonthChange(date);
    } else {
      // Uncontrolled mode - update internal state
      setInternalMonth(date);
    }
  };

  const filterUniqueDates = (dates: Date[]): Date[] => {
    return Object.values(
      dates.reduce((acc: { [key: string]: Date }, date: Date) => {
        const key = format(date, DATE_FORMAT);
        acc[key] = date;
        return acc;
      }, {})
    ).sort((a, b) => a.getTime() - b.getTime());
  };

  const generateAvailableDateRange = (startDate: Date, endDate: Date): Date[] => {
    const range = eachDayOfInterval({
      start: startDate < endDate ? startDate : endDate,
      end: startDate < endDate ? endDate : startDate,
    });

    return range.filter((date) => {
      const dateKey = format(date, DATE_FORMAT);
      return availableDatesSet.has(dateKey);
    });
  };

  const handleDayClick: DayClickEventHandler = (date, modifiers) => {
    if (disabled || modifiers.disabled) {
      return;
    }

    const dateKey = format(date, DATE_FORMAT);
    if (!availableDatesSet.has(dateKey)) {
      return;
    }

    let datesToSelect: Date[] = [];

    if (shiftHeld && previousSelectedDate) {
      // Shift-click: select range between previous date and current date
      const range = generateAvailableDateRange(previousSelectedDate, date);
      datesToSelect = filterUniqueDates([...selectedDates, ...range]);
      setPreviousSelectedDate(null); // Clear range start after selection
    } else {
      // Normal click: toggle single date
      if (modifiers.selected) {
        // Deselect: remove this date from selection
        datesToSelect = selectedDates.filter((selectedDate) => !isSameDay(selectedDate, date));
        setPreviousSelectedDate(null);
      } else {
        // Select: add this date to selection
        datesToSelect = filterUniqueDates([...selectedDates, date]);
        setPreviousSelectedDate(date); // Set as potential range start for next shift-click
      }
    }

    onSelectedDatesChange(datesToSelect);
  };

  const calendarClassName = disabled
    ? className
      ? `${className} rdp-disabled`
      : 'rdp-disabled'
    : className;

  return (
    <DayPicker
      mode="multiple"
      selected={selectedDates}
      disabled={disabledDates}
      month={displayMonth}
      fromDate={effectiveFromDate}
      numberOfMonths={numberOfMonths}
      className={calendarClassName}
      modifiers={{
        available: availableDatesAsDates,
      }}
      modifiersClassNames={{
        available: 'rdp-day_available',
      }}
      onDayClick={handleDayClick}
      onMonthChange={handleMonthChange}
      weekStartsOn={0}
    />
  );
}
